<script setup>
import {reactive, watch} from 'vue'

const props = defineProps({
    current: { required: true },
    chart: { required: true },
    arrange: { type: String, required: true },
    toothId: { type: String, required: true },
    x: { type: Number, default: 0 },
    y: { type: Number, default: 0 },
})
const emits = defineEmits(['teeth-selected-area'])

const selected = reactive({
    d: false,
    b: false,
    m: false,
    l: false,
    o: false,
    r: false,
    a: false,
})

watch(
    () => props.current,
    (newVal) => {
        for (const key in selected) delete selected[key]
        if (newVal) Object.assign(selected, newVal)
    },
    { immediate: true }
)

function select(area) {
    if (selected.a && (area !== 'r' && area !== 'a')) {
        return;
    }
    selected[area] = !selected[area]

    if (selected.b && selected.d && selected.m && selected.l && selected.o) {
        selected.a = true;
        selected.b = selected.d = selected.m = selected.l = selected.o = false
    }

    emits('teeth-selected-area', { toothId: props.toothId, selected: { ...selected } })
}
</script>

<template>
    <g v-if="arrange === 'top' || arrange === 'bottom'" :transform="arrange === 'top' ? `translate(${x}, ${85 + y}) scale(0.45,-0.45)` :  `translate(${x},${106 + y}) scale(0.45,0.45)`" fill="white" stroke="dimgray" stroke-width="2" @dblclick="select('a')">
        <rect :id="`${toothId}-shape-a`" class="tooth-all" :class="{ 'tooth-selected-no-need-now' : selected.a }" x="0" y="0" width="100" height="160" stroke="white" @click="select('a')"></rect>
        <path :id="`${toothId}-shape-d`" class="tooth-edge" :class="{ 'tooth-selected' : selected.a || selected.d }" :fill="chart['d'] ? chart['d'].shape : ''" d="M0 0 L50 50 L0 100 Z" @click="select('d')"></path>
        <path :id="`${toothId}-shape-l`" class="tooth-edge" :class="{ 'tooth-selected' : selected.a || selected.l }" :fill="chart['l'] ? chart['l'].shape : ''" d="M0 0 L50 50 L100 0 Z" @click="select('l')"></path>
        <path :id="`${toothId}-shape-m`" class="tooth-edge" :class="{ 'tooth-selected' : selected.a || selected.m }" :fill="chart['m'] ? chart['m'].shape : ''" d="M100 0 L50 50 L100 100 Z" @click="select('m')"></path>
        <path :id="`${toothId}-shape-b`" class="tooth-edge" :class="{ 'tooth-selected' : selected.a || selected.b }" :fill="chart['b'] ? chart['b'].shape : ''" d="M100 100 L50 50 L0 100 Z" @click="select('b')"></path>
        <rect :id="`${toothId}-shape-o`" class="tooth-edge" :class="{ 'tooth-selected' : selected.a || selected.o }" :fill="chart['o'] ? chart['o'].shape : ''" x="30" y="30" width="40" height="40" @click="select('o')"></rect>
        <path :id="`${toothId}-shape-r`" class="tooth-root" :class="{ 'tooth-selected' : selected.r }" :fill="chart['r'] ? chart['r'].shape : ''" d="M0 103 L30 133 L70 133 L100 103 Z" stroke="white" @click="select('r')"></path>

        <text :id="`${toothId}-text-d`"  :class="`text-${toothId}-d`" text-anchor="middle" :fill="chart['d'] ? chart['d'].text.style : ''"  alignment-baseline="central" x="15" y="50" style="font-weight: bold; font-size: 25px; font-family: sans-serif; pointer-events:none;shape-rendering:crispEdges;" transform="scale(-1,-1)" stroke="none" transform-origin="15px 50px">
            {{ chart['d'] ? chart['d'].text.content : '' }}
        </text>
        <text :id="`${toothId}-text-l`" :class="`text-${toothId}-l`" text-anchor="middle" :fill="chart['l'] ? chart['l'].text.style : ''"  alignment-baseline="central" x="50" y="15" style="font-weight: bold; font-size: 25px; font-family: sans-serif; pointer-events:none;shape-rendering:crispEdges;" transform="scale(-1,-1)" stroke="none" transform-origin="50px 15px">
            {{ chart['l'] ? chart['l'].text.content : '' }}
        </text>
        <text :id="`${toothId}-text-m`" :class="`text-${toothId}-m`" text-anchor="middle" :fill="chart['m'] ? chart['m'].text.style : ''"  alignment-baseline="central" x="85" y="50" style="font-weight: bold; font-size: 25px; font-family: sans-serif; pointer-events:none;shape-rendering:crispEdges;" transform="scale(-1,-1)" stroke="none" transform-origin="85px 50px">
            {{ chart['m'] ? chart['m'].text.content : '' }}
        </text>
        <text :id="`${toothId}-text-b`" :class="`text-${toothId}-b`" text-anchor="middle" :fill="chart['b'] ? chart['b'].text.style : ''"  alignment-baseline="central" x="50" y="85" style="font-weight: bold; font-size: 25px; font-family: sans-serif; pointer-events:none;shape-rendering:crispEdges;" transform="scale(-1,-1)" stroke="none" transform-origin="50px 85px">
            {{ chart['b'] ? chart['b'].text.content : '' }}
        </text>
        <text :id="`${toothId}-text-o`" :class="`text-${toothId}-o`" text-anchor="middle" :fill="chart['o'] ? chart['o'].text.style : ''"  alignment-baseline="central" x="50" y="50" style="font-weight: bold; font-size: 25px; font-family: sans-serif; pointer-events:none;shape-rendering:crispEdges;" transform="scale(-1,-1)" stroke="none" transform-origin="50px 50px">
            {{ chart['o'] ? chart['o'].text.content : '' }}
        </text>
        <text :id="`${toothId}-text-r`" :class="`text-${toothId}-r`" text-anchor="middle" :fill="chart['r'] ? chart['r'].text.style : ''"  alignment-baseline="central" x="50" y="115" style="font-weight: bold; font-size: 25px; font-family: sans-serif; pointer-events:none;shape-rendering:crispEdges;" transform="scale(-1,-1)" stroke="none" transform-origin="50px 115px">
            {{ chart['r'] ? chart['r'].text.content : '' }}
        </text>
        <text :id="`${toothId}-text-a`" :class="`text-${toothId}-a`" text-anchor="middle" :fill="chart['a'] ? chart['a'].text.style : ''"  alignment-baseline="central" x="50" y="50" style="font-weight: bold;  font-size: 115px; font-family: sans-serif; pointer-events:none;shape-rendering:crispEdges;" transform="scale(-1,-1)" stroke="none" transform-origin="50px 50px">
            {{ chart['a'] ? chart['a'].text.content : '' }}
        </text>
    </g>
</template>

<style scoped>
    .tooth-edge, .tooth-root, .tooth-all {
        cursor: pointer;
        transition: fill .12s
    }
    .tooth-edge:hover, .tooth-root:hover, .tooth-all:hover, .tooth-all:hover ~ .tooth-edge {
        stroke: red;
        stroke-width: 3;
    }
    .tooth-selected {
        stroke-width: 5;
        stroke: black;
        stroke-linejoin: round;
        stroke-linecap: round;
    }
</style>
